// useLinkPreview — thin hook over `getUrlPreview` with a module-level
// cache so every row that encounters the same URL shares a single
// fetch for the lifetime of the tab.
//
// Why module-level and not context:
//   A preview is a pure function of the URL (Synapse caches the
//   upstream fetch keyed on the URL itself), so the cache has no
//   per-user or per-room scope. Dropping it into the same lifecycle
//   as the React tree means every `<LinkPreviewCard>` unmount would
//   have to coordinate against every other mount to avoid
//   double-fetches. A plain map behind the hook is the smaller
//   correct seam.
//
// Memory bounds:
//   `Map` iteration is insertion-ordered, which makes evicting the
//   oldest entry constant-time. We cap at `CACHE_CAP` so a
//   pathological room (a bot posting thousands of unique URLs) can't
//   leak memory indefinitely. Eviction is insert-only FIFO — a cache
//   hit does NOT promote the entry to the newest slot, and a pending
//   entry is never evicted so an in-flight fetch is never displaced
//   by a concurrent request for a different URL. The primary failure
//   mode is "unbounded unique URLs", not "working set", and plain
//   FIFO is enough to bound the cost. The cap is therefore soft: if
//   every entry is pending (pathological), the cache is allowed to
//   grow past `CACHE_CAP` until the in-flight fetches settle.
//
// Fail-silent contract:
//   The wrapper always resolves with `LinkPreview | null`. `null` is
//   cached so a broken link isn't retried on every re-render. If the
//   underlying binding ever rejects (future regression), the `.catch`
//   guard below records `null` so subscribers don't wedge forever.
//   If the card is unmounted mid-fetch, the in-flight promise
//   continues and its result lands in the cache for the next mount;
//   the unmounted component simply drops its state update.
//
// Visibility gating is NOT the hook's job — the caller decides when
// to mount the card. Off-screen rows keep the card unmounted, so the
// fetch never starts. See `LinkPreviewCard` in `components/LinkPreview`.

import { useEffect, useState } from "react";

import { getUrlPreview, type Client, type LinkPreview } from "~/lib/matrix";

type CacheEntry =
  | { status: "pending"; promise: Promise<LinkPreview | null> }
  | { status: "resolved"; value: LinkPreview | null };

/**
 * Upper bound on distinct cached URLs. A long chat session routinely
 * encounters a few hundred unique links; 1024 carries that case
 * without memory pressure and still draws a clear line against
 * pathological inputs.
 */
const CACHE_CAP = 1024;

const cache = new Map<string, CacheEntry>();

export interface UseLinkPreviewResult {
  preview: LinkPreview | null;
  /** True while the first fetch for this URL is in flight. Subsequent
   *  mounts that hit the resolved cache never see `true` — they go
   *  straight to `{ preview, isLoading: false }`. */
  isLoading: boolean;
}

/**
 * Fetch a preview for `url` via Synapse. Returns the cached value
 * synchronously if one already exists (`isLoading: false` on first
 * render); otherwise mounts a subscription to the in-flight promise
 * and flips state when it resolves.
 *
 * `null` `client` short-circuits to `{ preview: null, isLoading:
 * false }` — useful while the shell is still booting and the
 * timeline is rendering fixture stories. `null` `url` behaves the
 * same way so the hook can be called unconditionally inside a
 * `.map` without a guard at every call site.
 *
 * `ts` is the event's `origin_server_ts`. Synapse keys its
 * per-event preview cache on this value, so threading it through
 * means two rows rendering the same URL at roughly the same time
 * share one origin hit. The cache key stays the URL alone — adding
 * `ts` would defeat the whole point of deduplicating across rows.
 */
export function useLinkPreview(
  client: Client | null,
  url: string | null,
  ts?: number,
): UseLinkPreviewResult {
  const initial = resolveInitial(client, url);
  const [state, setState] = useState<UseLinkPreviewResult>(initial);

  useEffect(() => {
    // Re-resolve on client / url change. The initial state was already
    // computed from the current values — this effect covers the case
    // where the URL changes during the component's lifetime.
    const next = resolveInitial(client, url);
    if (next.isLoading === false) {
      setState(next);
      return;
    }
    if (!client || !url) return;

    let cancelled = false;
    const entry = ensureCacheEntry(client, url, ts);
    setState({ preview: null, isLoading: true });
    entry.promise.then((value) => {
      if (cancelled) return;
      setState({ preview: value, isLoading: false });
    });

    return () => {
      cancelled = true;
    };
    // `client` is the Matrix client singleton — it identity-changes
    // on sign-out, which is the correct signal to invalidate an
    // in-flight fetch. `ts` is omitted from the deps array
    // deliberately: it only affects the initial Synapse cache key
    // and re-fetching on a different ts for the same URL would
    // double-call the server with no benefit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, url]);

  return state;
}

function resolveInitial(client: Client | null, url: string | null): UseLinkPreviewResult {
  if (!client || !url) return { preview: null, isLoading: false };
  const existing = cache.get(url);
  if (existing && existing.status === "resolved") {
    return { preview: existing.value, isLoading: false };
  }
  return { preview: null, isLoading: true };
}

function ensureCacheEntry(
  client: Client,
  url: string,
  ts: number | undefined,
): { promise: Promise<LinkPreview | null> } {
  const existing = cache.get(url);
  if (existing && existing.status === "pending") return existing;
  if (existing && existing.status === "resolved") {
    return { promise: Promise.resolve(existing.value) };
  }
  // New fetch. `.catch` collapses any unexpected rejection into a
  // cached `null` — the wrapper documents resolve-null-on-error, but
  // a defensive guard keeps one bad URL from wedging the cache
  // entry in `"pending"` forever (every subsequent mount would
  // otherwise subscribe to a promise that never settles).
  //
  // Cap enforcement runs on insert AND on settle. The insert pass
  // trims resolved entries so the steady state stays at `CACHE_CAP`;
  // the settle pass catches the case where a burst of concurrent
  // fetches pushed the cache past the cap (pending entries were
  // skipped) and the fetches have now resolved — without the
  // settle-time re-check, the cache could stay permanently oversized
  // for a long session that sees no further novel URLs.
  const promise = getUrlPreview(client, url, ts)
    .then((value) => {
      cache.set(url, { status: "resolved", value });
      enforceCacheCap();
      return value;
    })
    .catch(() => {
      cache.set(url, { status: "resolved", value: null });
      enforceCacheCap();
      return null;
    });
  enforceCacheCap();
  cache.set(url, { status: "pending", promise });
  return { promise };
}

/**
 * FIFO eviction. We delete the oldest RESOLVED key BEFORE inserting
 * the new one so the cap is strict — the map never holds
 * `CACHE_CAP + 1` entries even transiently. Pending entries are
 * skipped to avoid de-duplicating an in-flight fetch: evicting a
 * `"pending"` entry would let a concurrent subscriber start a
 * second identical round-trip to the homeserver. In the corner case
 * where every entry is pending (pathological — ~1024 unique URLs
 * fetching simultaneously), we allow the cap to flex rather than
 * break the dedupe contract; resolved entries displace older
 * pending ones as fetches settle.
 */
function enforceCacheCap(): void {
  if (cache.size < CACHE_CAP) return;
  for (const [key, entry] of cache) {
    if (entry.status === "resolved") {
      cache.delete(key);
      if (cache.size < CACHE_CAP) return;
    }
  }
}

/** Test-only: drop every cached entry. Exposed so unit tests can
 *  simulate a fresh tab without reloading the whole module. */
export function _resetLinkPreviewCache(): void {
  cache.clear();
}
